import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    FormHelperText
} from "@material-ui/core";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";

// services
import ShippingImportRulesService from "../../services/ShippingImportRulesService";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

const useStyles = makeStyles(styles);

const validationSchema = yup.object({
    countryCode: yup.string().length(2, "Country code must be exactly 2 characters").required("Country code is required"),
    regimeName: yup.string().required("Regime name is required"),
    currency: yup.string().length(3, "Currency must be exactly 3 characters").required("Currency is required"),
    shippingBaseRate: yup.number().min(0, "Rate cannot be negative").required("Required"),
    shippingVolumetricRate: yup.number().min(0, "Rate cannot be negative").required("Required"),
    shippingMinWeight: yup.number().min(0, "Weight cannot be negative").required("Required"),
    shippingMinCharge: yup.number().min(0, "Charge cannot be negative").required("Required"),
    maxMerchandiseValue: yup.number().min(0, "Value cannot be negative").required("Required"),
    maxWeightKg: yup.number().min(0, "Weight cannot be negative").required("Required"),
    maxUnitsPerReference: yup.number().integer("Must be an integer").min(0, "Quantity cannot be negative").required("Required"),
    taxExemptionThreshold: yup.number().min(0, "Cannot be negative").required("Required"),
    customsDutyPercentage: yup.number().min(0, "Cannot be negative").max(100, "Cannot exceed 100").required("Required"),
    vatPercentage: yup.number().min(0, "Cannot be negative").max(100, "Cannot exceed 100").required("Required"),
    startDate: yup.string().nullable(),
    endDate: yup.string().nullable()
});

const countriesList = [
    { code: "CO", name: "Colombia" },
    { code: "CL", name: "Chile" },
    { code: "MX", name: "México" },
    { code: "BR", name: "Brasil" },
    { code: "PE", name: "Perú" },
    { code: "AR", name: "Argentina" },
    { code: "EC", name: "Ecuador" },
    { code: "VE", name: "Venezuela" },
    { code: "CR", name: "Costa Rica" },
    { code: "PA", name: "Panamá" },
    { code: "UY", name: "Uruguay" },
    { code: "PY", name: "Paraguay" },
    { code: "BO", name: "Bolivia" }
];

const currenciesList = ["USD", "COP", "CLP", "MXN", "BRL", "PEN", "ARS", "VES", "CRC", "PAB", "UYU", "PYG", "BOB"];

export default function CreateEditShippingImportRule({ isEdit, initialValues = {}, onSuccess, handleClose }) {
    const classes = useStyles();

    // Helper to format date object or string into YYYY-MM-DD for native input
    const formatDate = (dateVal) => {
        if (!dateVal) return "";
        // If it has timezone, split it at T
        if (typeof dateVal === "string") {
            return dateVal.split("T")[0];
        }
        return dateVal;
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: initialValues.id || 0,
            countryCode: initialValues.countryCode || "",
            regimeName: initialValues.regimeName || "",
            currency: initialValues.currency || "USD",
            shippingBaseRate: initialValues.shippingBaseRate !== undefined ? initialValues.shippingBaseRate : 0,
            shippingVolumetricRate: initialValues.shippingVolumetricRate !== undefined ? initialValues.shippingVolumetricRate : 0,
            shippingMinWeight: initialValues.shippingMinWeight !== undefined ? initialValues.shippingMinWeight : 0,
            shippingMinCharge: initialValues.shippingMinCharge !== undefined ? initialValues.shippingMinCharge : 0,
            maxMerchandiseValue: initialValues.maxMerchandiseValue !== undefined ? initialValues.maxMerchandiseValue : 0,
            maxWeightKg: initialValues.maxWeightKg !== undefined ? initialValues.maxWeightKg : 0,
            maxUnitsPerReference: initialValues.maxUnitsPerReference !== undefined ? initialValues.maxUnitsPerReference : 0,
            taxExemptionThreshold: initialValues.taxExemptionThreshold !== undefined ? initialValues.taxExemptionThreshold : 0,
            customsDutyPercentage: initialValues.customsDutyPercentage !== undefined ? initialValues.customsDutyPercentage : 0,
            vatPercentage: initialValues.vatPercentage !== undefined ? initialValues.vatPercentage : 0,
            startDate: formatDate(initialValues.startDate),
            endDate: formatDate(initialValues.endDate),
            isActive: initialValues.isActive === 1 || initialValues.isActive === true
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                const payload = {
                    ...values,
                    countryCode: values.countryCode.toUpperCase(),
                    currency: values.currency.toUpperCase(),
                    isActive: values.isActive ? 1 : 0,
                    startDate: values.startDate || null,
                    endDate: values.endDate || null
                };

                let responseRule;
                if (isEdit) {
                    const res = await ShippingImportRulesService.put(values.id, payload);
                    responseRule = res.data;
                } else {
                    const res = await ShippingImportRulesService.post(payload);
                    responseRule = res.data;
                }
                onSuccess(responseRule);
            } catch (error) {
                console.error("Error saving rule:", error);
                const serverMsg = error.response?.data?.message || "An error occurred while saving the rule.";
                alert(serverMsg);
            }
        }
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <GridContainer>
                {/* Row 1: Country & Regime */}
                <GridItem xs={12} sm={4}>
                    <FormControl variant="standard" fullWidth style={{ marginTop: 16 }}>
                        <InputLabel id="country-select-label">Country</InputLabel>
                        <Select
                            labelId="country-select-label"
                            id="countryCode"
                            name="countryCode"
                            value={formik.values.countryCode}
                            onChange={formik.handleChange}
                            error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                        >
                            <MenuItem value="" disabled>Select Country</MenuItem>
                            {countriesList.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                    {country.name} ({country.code})
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.countryCode && formik.errors.countryCode && (
                            <FormHelperText error>{formik.errors.countryCode}</FormHelperText>
                        )}
                    </FormControl>
                </GridItem>

                <GridItem xs={12} sm={5}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="regimeName"
                        name="regimeName"
                        label="Regime Name"
                        value={formik.values.regimeName}
                        onChange={formik.handleChange}
                        error={formik.touched.regimeName && Boolean(formik.errors.regimeName)}
                        helperText={formik.touched.regimeName && formik.errors.regimeName}
                    />
                </GridItem>

                <GridItem xs={12} sm={3}>
                    <FormControl variant="standard" fullWidth style={{ marginTop: 16 }}>
                        <InputLabel id="currency-select-label">Currency</InputLabel>
                        <Select
                            labelId="currency-select-label"
                            id="currency"
                            name="currency"
                            value={formik.values.currency}
                            onChange={formik.handleChange}
                            error={formik.touched.currency && Boolean(formik.errors.currency)}
                        >
                            {currenciesList.map((cur) => (
                                <MenuItem key={cur} value={cur}>
                                    {cur}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.currency && formik.errors.currency && (
                            <FormHelperText error>{formik.errors.currency}</FormHelperText>
                        )}
                    </FormControl>
                </GridItem>

                {/* Row 2: Shipping Rates & Limits */}
                <GridItem xs={12} sm={3}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="shippingBaseRate"
                        name="shippingBaseRate"
                        label="Base Shipping Rate / physical lb"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={formik.values.shippingBaseRate}
                        onChange={formik.handleChange}
                        error={formik.touched.shippingBaseRate && Boolean(formik.errors.shippingBaseRate)}
                        helperText={formik.touched.shippingBaseRate && formik.errors.shippingBaseRate}
                    />
                </GridItem>

                <GridItem xs={12} sm={3}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="shippingVolumetricRate"
                        name="shippingVolumetricRate"
                        label="Volumetric Diff Rate / lb"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={formik.values.shippingVolumetricRate}
                        onChange={formik.handleChange}
                        error={formik.touched.shippingVolumetricRate && Boolean(formik.errors.shippingVolumetricRate)}
                        helperText={formik.touched.shippingVolumetricRate && formik.errors.shippingVolumetricRate}
                    />
                </GridItem>

                <GridItem xs={12} sm={3}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="shippingMinWeight"
                        name="shippingMinWeight"
                        label="Min Billable Weight (lb)"
                        type="number"
                        inputProps={{ step: "0.1", min: "0" }}
                        value={formik.values.shippingMinWeight}
                        onChange={formik.handleChange}
                        error={formik.touched.shippingMinWeight && Boolean(formik.errors.shippingMinWeight)}
                        helperText={formik.touched.shippingMinWeight && formik.errors.shippingMinWeight}
                    />
                </GridItem>

                <GridItem xs={12} sm={3}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="shippingMinCharge"
                        name="shippingMinCharge"
                        label="Min Shipping Charge"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={formik.values.shippingMinCharge}
                        onChange={formik.handleChange}
                        error={formik.touched.shippingMinCharge && Boolean(formik.errors.shippingMinCharge)}
                        helperText={formik.touched.shippingMinCharge && formik.errors.shippingMinCharge}
                    />
                </GridItem>

                {/* Row 3: Regime Limits */}
                <GridItem xs={12} sm={4}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="maxMerchandiseValue"
                        name="maxMerchandiseValue"
                        label="Max Merchandise Value"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={formik.values.maxMerchandiseValue}
                        onChange={formik.handleChange}
                        error={formik.touched.maxMerchandiseValue && Boolean(formik.errors.maxMerchandiseValue)}
                        helperText={formik.touched.maxMerchandiseValue && formik.errors.maxMerchandiseValue}
                    />
                </GridItem>

                <GridItem xs={12} sm={4}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="maxWeightKg"
                        name="maxWeightKg"
                        label="Max Weight (kg)"
                        type="number"
                        inputProps={{ step: "0.1", min: "0" }}
                        value={formik.values.maxWeightKg}
                        onChange={formik.handleChange}
                        error={formik.touched.maxWeightKg && Boolean(formik.errors.maxWeightKg)}
                        helperText={formik.touched.maxWeightKg && formik.errors.maxWeightKg}
                    />
                </GridItem>

                <GridItem xs={12} sm={4}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="maxUnitsPerReference"
                        name="maxUnitsPerReference"
                        label="Max Units / reference"
                        type="number"
                        inputProps={{ step: "1", min: "0" }}
                        value={formik.values.maxUnitsPerReference}
                        onChange={formik.handleChange}
                        error={formik.touched.maxUnitsPerReference && Boolean(formik.errors.maxUnitsPerReference)}
                        helperText={formik.touched.maxUnitsPerReference && formik.errors.maxUnitsPerReference}
                    />
                </GridItem>

                {/* Row 4: Tax Exemption Threshold */}
                <GridItem xs={12}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="taxExemptionThreshold"
                        name="taxExemptionThreshold"
                        label="Tax Exemption Threshold (USD)"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={formik.values.taxExemptionThreshold}
                        onChange={formik.handleChange}
                        error={formik.touched.taxExemptionThreshold && Boolean(formik.errors.taxExemptionThreshold)}
                        helperText={
                            (formik.touched.taxExemptionThreshold && formik.errors.taxExemptionThreshold) ||
                            "Merchandise value below this amount is exempt from customs duty and VAT. Set to 0 to always apply taxes."
                        }
                    />
                </GridItem>

                {/* Row 5: Tax Percentages */}
                <GridItem xs={12} sm={6}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="customsDutyPercentage"
                        name="customsDutyPercentage"
                        label="Customs Duty Percentage (%)"
                        type="number"
                        inputProps={{ step: "0.01", min: "0", max: "100" }}
                        value={formik.values.customsDutyPercentage}
                        onChange={formik.handleChange}
                        error={formik.touched.customsDutyPercentage && Boolean(formik.errors.customsDutyPercentage)}
                        helperText={formik.touched.customsDutyPercentage && formik.errors.customsDutyPercentage}
                    />
                </GridItem>

                <GridItem xs={12} sm={6}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="vatPercentage"
                        name="vatPercentage"
                        label="VAT Percentage (%)"
                        type="number"
                        inputProps={{ step: "0.01", min: "0", max: "100" }}
                        value={formik.values.vatPercentage}
                        onChange={formik.handleChange}
                        error={formik.touched.vatPercentage && Boolean(formik.errors.vatPercentage)}
                        helperText={formik.touched.vatPercentage && formik.errors.vatPercentage}
                    />
                </GridItem>

                {/* Row 5: Validity Dates & Status */}
                <GridItem xs={12} sm={4}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="startDate"
                        name="startDate"
                        label="Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.startDate}
                        onChange={formik.handleChange}
                        error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                        helperText={formik.touched.startDate && formik.errors.startDate}
                    />
                </GridItem>

                <GridItem xs={12} sm={4}>
                    <TextField
                        fullWidth
                        margin="normal"
                        id="endDate"
                        name="endDate"
                        label="End Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.endDate}
                        onChange={formik.handleChange}
                        error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                        helperText={formik.touched.endDate && formik.errors.endDate}
                    />
                </GridItem>

                <GridItem xs={12} sm={4} style={{ display: "flex", alignItems: "center", paddingTop: 16 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="isActive"
                                name="isActive"
                                checked={formik.values.isActive}
                                onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Active Status"
                    />
                </GridItem>
            </GridContainer>

            <GridContainer style={{ marginTop: 24, justifyContent: "center" }}>
                <GridItem xs={12} style={{ textAlign: "center" }}>
                    <Button type="submit" color="rose" style={{ marginRight: 8 }}>
                        Save and Close
                    </Button>
                    <Button type="button" color="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                </GridItem>
            </GridContainer>
        </form>
    );
}
